import React, { useEffect, useRef, useCallback } from 'react';

const NODE_COUNT = 40;
const CONNECTION_DISTANCE = 160;
const SPEED = 0.12;

export default function NetworkBackground() {
  const canvasRef = useRef(null);
  const nodesRef = useRef([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const rafRef = useRef(null);
  const scrollRef = useRef(0);

  const initNodes = useCallback((w, h) => {
    nodesRef.current = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * SPEED,
      vy: (Math.random() - 0.5) * SPEED,
      radius: Math.random() * 1.5 + 0.5,
      brightness: Math.random() * 0.4 + 0.1,
      pulseOffset: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = document.documentElement.scrollHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = document.documentElement.scrollHeight + 'px';
      ctx.scale(dpr, dpr);
      if (nodesRef.current.length === 0) {
        initNodes(window.innerWidth, document.documentElement.scrollHeight);
      }
    };

    resize();

    const handleMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY + window.scrollY };
    };
    const handleScroll = () => { scrollRef.current = window.scrollY; };

    window.addEventListener('mousemove', handleMouse);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resize);

    const w = () => window.innerWidth;
    const h = () => document.documentElement.scrollHeight;

    const animate = (time) => {
      const t = time * 0.001;
      ctx.clearRect(0, 0, w(), h());
      const nodes = nodesRef.current;
      const viewTop = scrollRef.current;
      const viewBottom = viewTop + window.innerHeight;

      // Update positions
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w()) n.vx *= -1;
        if (n.y < 0 || n.y > h()) n.vy *= -1;
        n.x = Math.max(0, Math.min(w(), n.x));
        n.y = Math.max(0, Math.min(h(), n.y));
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        // Skip nodes far from viewport
        if (a.y < viewTop - 300 || a.y > viewBottom + 300) continue;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          if (b.y < viewTop - 300 || b.y > viewBottom + 300) continue;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DISTANCE) {
            const alpha = (1 - dist / CONNECTION_DISTANCE) * 0.08;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(0, 212, 255, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach((n) => {
        if (n.y < viewTop - 100 || n.y > viewBottom + 100) return;
        const pulse = Math.sin(t * 1.5 + n.pulseOffset) * 0.3 + 0.7;
        const alpha = n.brightness * pulse;

        // Mouse proximity glow
        const mx = mouseRef.current.x;
        const my = mouseRef.current.y;
        const mouseDist = Math.sqrt((n.x - mx) ** 2 + (n.y - my) ** 2);
        const mouseGlow = mouseDist < 200 ? (1 - mouseDist / 200) * 0.5 : 0;

        // Outer glow
        if (mouseGlow > 0) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.radius * 6, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 212, 255, ${mouseGlow * 0.15})`;
          ctx.fill();
        }

        // Node
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius + mouseGlow * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${alpha + mouseGlow})`;
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', resize);
    };
  }, [initNodes]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="network-background"
      className="pointer-events-none fixed inset-0 z-0"
      style={{ 
        opacity: 0.5,
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    />
  );
}
